import { BillingInfoFieldName } from '@shipstation/financial-services-ui-components';

import { FIELD_NAMES } from '../address/address-fields/constants';

/** @typedef {import('@shipstation/financial-services-ui-components').BillingInfoData} BillingInfoData */
/** @typedef {import('./types/fsui').BillingAddress} BillingAddress */
/** @typedef {import('./types/fsui').ActivePaymentMethodsResponse} ActivePaymentMethodsResponse */
/** @typedef {import('@shipstation/financial-services-ui-components').StoredPaymentMethod} StoredPaymentMethod */

export const fsuiToSsuiAddressFieldMap = {
  [BillingInfoFieldName.City]: FIELD_NAMES.CITY,
  [BillingInfoFieldName.Company]: FIELD_NAMES.COMPANY,
  [BillingInfoFieldName.CountryCode]: FIELD_NAMES.COUNTRY_CODE,
  [BillingInfoFieldName.FullName]: FIELD_NAMES.NAME,
  [BillingInfoFieldName.Line1]: FIELD_NAMES.LINE1,
  [BillingInfoFieldName.Line2]: FIELD_NAMES.LINE2,
  [BillingInfoFieldName.Line3]: FIELD_NAMES.LINE3,
  [BillingInfoFieldName.Name]: FIELD_NAMES.NAME,
  [BillingInfoFieldName.Phone]: FIELD_NAMES.PHONE,
  [BillingInfoFieldName.PostalCode]: FIELD_NAMES.POSTAL_CODE,
  [BillingInfoFieldName.State]: FIELD_NAMES.STATE,
  [BillingInfoFieldName.Email]: FIELD_NAMES.EMAIL,
};

export const mapWalletFormBillingAddressToFsuiBillingInfo = (walletFormBillingAddress) => {
  if (!walletFormBillingAddress) {
    return;
  }
  const newBillingInfo = {};
  Object.entries(fsuiToSsuiAddressFieldMap).forEach(([fsuiField, ssuiField]) => {
    newBillingInfo[fsuiField] = walletFormBillingAddress[ssuiField] || '';
  });
  return newBillingInfo;
};

/**
 * Maps the FSUI billing info data to a Station BillingAddress object.
 * @param {BillingInfoData} billingInfo
 * @returns {BillingAddress | undefined}
 */
export const mapBillingInfoDataToBillingAddress = (billingInfo) => {
  if (!billingInfo) {
    return;
  }
  return {
    fullName: billingInfo[BillingInfoFieldName.FullName],
    company: billingInfo[BillingInfoFieldName.Company],
    addressLine1: billingInfo[BillingInfoFieldName.Line1],
    addressLine2: billingInfo[BillingInfoFieldName.Line2],
    cityLocality: billingInfo[BillingInfoFieldName.City],
    stateProvince: billingInfo[BillingInfoFieldName.State],
    postalCode: billingInfo[BillingInfoFieldName.PostalCode],
    countryCode: billingInfo[BillingInfoFieldName.CountryCode],
    phone: billingInfo[BillingInfoFieldName.Phone],
    email: billingInfo[BillingInfoFieldName.Email],
  };
};

export const getBillingInfoFromPaymentMethod = (paymentMethod, { name, homeCountry, phone, companyName }) => {
  if (!paymentMethod || !paymentMethod.accountHolderAddress) {
    return null;
  }

  const address = paymentMethod.accountHolderAddress;

  return {
    [BillingInfoFieldName.FullName]: paymentMethod.accountHolderName ?? name,
    [BillingInfoFieldName.Company]: paymentMethod.companyName ?? companyName,
    [BillingInfoFieldName.Line1]: address.addressLine1,
    [BillingInfoFieldName.Line2]: address.addressLine2,
    [BillingInfoFieldName.City]: address.cityLocality,
    [BillingInfoFieldName.State]: address.stateProvince,
    [BillingInfoFieldName.PostalCode]: address.postalCode,
    [BillingInfoFieldName.CountryCode]: address.countryCode ?? homeCountry,
    [BillingInfoFieldName.Phone]: paymentMethod.phoneNumber ?? phone,
  };
};

export const getBillingInfoFromShipFrom = (address) => {
  if (!address) {
    return null;
  }

  const { name, company, line1, line2, city, state, postalCode, countryCode, phone } = address;

  return {
    [BillingInfoFieldName.FullName]: name,
    [BillingInfoFieldName.Company]: company,
    [BillingInfoFieldName.Line1]: line1,
    [BillingInfoFieldName.Line2]: line2,
    [BillingInfoFieldName.City]: city,
    [BillingInfoFieldName.State]: state,
    [BillingInfoFieldName.PostalCode]: postalCode,
    [BillingInfoFieldName.CountryCode]: countryCode,
    [BillingInfoFieldName.Phone]: phone,
  };
};

/**
 * Reconstructs payment methods to ensure they have the correct structure.
 * @param {StoredPaymentMethod[] | null} paymentMethods
 * @returns {ActivePaymentMethodsResponse}
 **/
export const getPaymentMethodsFromArray = (paymentMethods) => {
  if (paymentMethods?.length > 0) {
    const activePaymentMethods = paymentMethods.filter((method) => method.assignedPreferences?.length > 0);
    const recentPaymentMethods = paymentMethods.filter((method) => !method.assignedPreferences || method.assignedPreferences.length === 0);
    return {
      activePaymentMethods,
      recentPaymentMethods,
    };
  }
  return {
    activePaymentMethods: [],
    recentPaymentMethods: [],
  };
};
